<script lang="ts">
const loadedImageSources = new Set<string>()
const loadedPreviewSources = new Set<string>()
</script>

<script setup lang="ts">
import { computed, ref, shallowRef, watch } from 'vue'
import { useElementWidth } from '@/composables/useElementWidth'
import {
  buildDisplayMediaImageUrl,
  buildLowQualityImagePreviewUrl,
  resolveMediaPreviewWidth,
  resolveMediaRequestWidth,
} from '@/utils/distribute/media-url'
import type { Props } from './type'

defineOptions({
  name: 'DreamaProgressiveImage',
})

const props = withDefaults(defineProps<Props>(), {
  src: null,
  alt: '',
  aspectRatio: null,
  objectFit: 'cover',
  loading: 'lazy',
  sourceType: 'image',
  priority: false,
  previewWidth: null,
  previewHeight: null,
  requestWidth: null,
  requestHeight: null,
})
const emit = defineEmits<{
  'preview-ready': []
  load: [src: string]
  error: [src: string | null]
}>()

const rootElement = ref<HTMLElement | null>(null)
const { width: layoutWidth } = useElementWidth(rootElement)
const previewLoaded = shallowRef(false)
const previewFailed = shallowRef(false)
const loaded = shallowRef(false)
const failed = shallowRef(false)
const hasEmittedPreviewReady = shallowRef(false)
const resolvedLayoutWidth = computed(() =>
  typeof props.requestWidth === 'number' && props.requestWidth > 0
    ? props.requestWidth
    : layoutWidth.value,
)
const requestWidth = computed(() =>
  resolveMediaRequestWidth(resolvedLayoutWidth.value, {
    dpr: typeof window === 'undefined' ? 1 : window.devicePixelRatio,
  }),
)
const requestHeight = computed(() =>
  typeof props.requestHeight === 'number' && props.requestHeight > 0 ? props.requestHeight : 0,
)
const previewWidth = computed(() =>
  typeof props.previewWidth === 'number' && props.previewWidth > 0
    ? props.previewWidth
    : resolveMediaPreviewWidth(resolvedLayoutWidth.value),
)
const previewHeight = computed(() =>
  typeof props.previewHeight === 'number' && props.previewHeight > 0 ? props.previewHeight : 0,
)
const rootStyle = computed(() => ({
  '--dreama-aspect': props.aspectRatio ?? 'auto',
}))
const displaySrc = computed(() =>
  buildDisplayMediaImageUrl(props.src, {
    height: requestHeight.value,
    sourceType: props.sourceType,
    width: requestWidth.value,
  }),
)
const previewSrc = computed(() =>
  buildLowQualityImagePreviewUrl(props.src, {
    height: previewHeight.value,
    sourceType: props.sourceType,
    width: previewWidth.value,
  }),
)
const shouldRenderPreview = computed(
  () =>
    Boolean(previewSrc.value) &&
    previewSrc.value !== displaySrc.value &&
    (!loaded.value || previewLoaded.value) &&
    !previewFailed.value,
)
const shouldHidePlaceholder = computed(() => (loaded.value || previewLoaded.value) && !failed.value)
const imageLoading = computed(() => (props.priority ? 'eager' : props.loading))
const imageFetchPriority = computed(() => (props.priority ? 'high' : 'auto'))
const previewLoading = computed(() => (props.priority ? 'eager' : 'lazy'))

function hasLoadedSource(src: string | null | undefined) {
  return Boolean(src && loadedImageSources.has(src))
}

function hasLoadedPreviewSource(src: string | null | undefined) {
  return Boolean(src && loadedPreviewSources.has(src))
}

function markLoaded() {
  if (displaySrc.value) {
    loadedImageSources.add(displaySrc.value)
    emit('load', displaySrc.value)
  }

  loaded.value = true
  emitPreviewReady()
}

function markPreviewLoaded() {
  if (previewSrc.value) {
    loadedPreviewSources.add(previewSrc.value)
  }

  previewLoaded.value = true
  emitPreviewReady()
}

function markPreviewFailed() {
  previewFailed.value = true
}

function markFailed() {
  failed.value = true
  emit('error', displaySrc.value)
  emitPreviewReady()
}

function emitPreviewReady() {
  if (
    hasEmittedPreviewReady.value ||
    (!shouldHidePlaceholder.value && (displaySrc.value || previewSrc.value) && !failed.value)
  ) {
    return
  }

  hasEmittedPreviewReady.value = true
  emit('preview-ready')
}

watch(
  () => [displaySrc.value, previewSrc.value],
  ([src, nextPreviewSrc]) => {
    loaded.value = hasLoadedSource(src)
    previewLoaded.value = hasLoadedPreviewSource(nextPreviewSrc)
    previewFailed.value = false
    failed.value = false
    hasEmittedPreviewReady.value = false
    emitPreviewReady()
  },
  { immediate: true },
)
</script>

<template>
  <div ref="rootElement" class="dreama-image" :style="rootStyle">
    <img
      v-if="displaySrc && !failed"
      class="dreama-image__img"
      :class="{ 'is-loaded': loaded }"
      :src="displaySrc"
      :alt="alt"
      :loading="imageLoading"
      :fetchpriority="imageFetchPriority"
      decoding="async"
      draggable="false"
      :style="{ objectFit }"
      @load="markLoaded"
      @error="markFailed"
    />
    <img
      v-if="shouldRenderPreview"
      class="dreama-image__preview"
      :class="{ 'is-loaded': previewLoaded }"
      :src="previewSrc ?? undefined"
      alt=""
      aria-hidden="true"
      :loading="previewLoading"
      fetchpriority="auto"
      decoding="async"
      draggable="false"
      :style="{ objectFit }"
      @load="markPreviewLoaded"
      @error="markPreviewFailed"
    />
    <div class="dreama-image__placeholder" :class="{ 'is-hidden': shouldHidePlaceholder }" />
    <div v-if="$slots.default" class="dreama-image__overlay">
      <slot />
    </div>
  </div>
</template>

<style scoped>
.dreama-image {
  position: relative;
  width: 100%;
  height: 100%;
  aspect-ratio: var(--dreama-aspect);
  overflow: hidden;
  background: var(--dreama-image-placeholder-bg, var(--app-skeleton-base));
}

.dreama-image__img,
.dreama-image__preview,
.dreama-image__placeholder,
.dreama-image__overlay {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}

.dreama-image__img,
.dreama-image__preview {
  user-select: none;
}

.dreama-image__img {
  z-index: 2;
  opacity: 0;
  transition: opacity 0.24s ease;
}

.dreama-image__img.is-loaded {
  opacity: 1;
}

.dreama-image__preview {
  z-index: 1;
  filter: blur(24px);
  opacity: 0;
  transform: scale(1.16);
  transition: opacity 0.16s ease;
}

.dreama-image__preview.is-loaded {
  opacity: 1;
}

.dreama-image__placeholder {
  z-index: 0;
  background: var(--dreama-image-placeholder-bg, var(--app-skeleton-base));
}

.dreama-image__placeholder.is-hidden {
  opacity: 0;
  transition: opacity 0.16s ease;
}

.dreama-image__overlay {
  z-index: 3;
}

@media (prefers-reduced-motion: reduce) {
  .dreama-image__img,
  .dreama-image__preview,
  .dreama-image__placeholder.is-hidden {
    transition: none;
  }
}
</style>
